import React, { useState, useEffect } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  OutlinedInput,
  Grid,
  ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { safeApiCall } from '../utils/api';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// 安全なfind()ヘルパー関数
const safeFindPrefecture = (prefectures, code) => {
  if (!Array.isArray(prefectures) || !code) return null;
  return prefectures.find(p => p && p.code === code) || null;
};

const safeFindCity = (cities, code) => {
  if (!Array.isArray(cities) || !code) return null;
  return cities.find(c => c && c.code === code) || null;
};

const safeFindCities = (cities, codes) => {
  if (!Array.isArray(cities) || !Array.isArray(codes)) return [];
  return codes.map(code => {
    const city = cities.find(c => c && c.code === code);
    return city || { code, name: code };
  });
};

const AreaSelector = ({ onAreaChange, onAgeConfirm, singleCitySelect = false, isGuestUser = false }) => {
  const [prefectures, setPrefectures] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedPrefecture, setSelectedPrefecture] = useState('');
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(''); // 単一選択用の状態
  const [isAgeConfirmed, setIsAgeConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);
  
  // 初期化状態を管理
  const [initializationState, setInitializationState] = useState({
    prefecturesLoaded: false,
    citiesLoaded: false,
    cacheLoaded: false,
    onAreaChangeCalled: false
  });

  // キャッシュから設定を読み込む（onAreaChangeは呼ばない）
  const loadCachedSettings = () => {
    try {
      const cachedSettings = localStorage.getItem('areaSettings');
      console.log('AreaSelector: キャッシュ確認中...', cachedSettings);
      
      if (cachedSettings) {
        const { prefecture, cities, city, ageConfirmed, timestamp } = JSON.parse(cachedSettings);
        const now = new Date().getTime();
        const oneMonth = 30 * 24 * 60 * 60 * 1000; // 1ヶ月のミリ秒

        console.log('AreaSelector: キャッシュデータ', { prefecture, city, timestamp });

        if (now - timestamp < oneMonth) {
          console.log('AreaSelector: キャッシュから設定を復元中...');
          console.log('復元データ:', { prefecture, city, cities, ageConfirmed });
          
          setSelectedPrefecture(prefecture || '');
          
          // エリア設定があるかどうかを確認してexpandedを設定
          const hasAreaSettings = prefecture && (singleCitySelect ? city : (cities && cities.length > 0));
          setExpanded(!hasAreaSettings); // エリア設定がない場合は開く
          
          if (singleCitySelect) {
            // 単一選択モード
            console.log('AreaSelector: 市区町村(単一選択)を復元:', city);
            setSelectedCity(city || '');
          } else {
            // 複数選択モード
            console.log('AreaSelector: 市区町村(複数選択)を復元:', cities);
            setSelectedCities(cities || []);
          }
          
          console.log('AreaSelector: 年齢確認状態を復元:', ageConfirmed);
          setIsAgeConfirmed(ageConfirmed || false);
          
          // 年齢確認状態をHome.jsに通知
          setTimeout(() => {
            onAgeConfirm?.(ageConfirmed || false);
          }, 100);
          
          // キャッシュ読み込み完了をマーク
          setInitializationState(prev => ({ ...prev, cacheLoaded: true }));
        } else {
          // 1ヶ月以上経過している場合はキャッシュをクリア
          console.log('AreaSelector: キャッシュが期限切れのため削除');
          localStorage.removeItem('areaSettings');
          // エリア設定がない場合は開いた状態にする
          setExpanded(true);
          setInitializationState(prev => ({ ...prev, cacheLoaded: true }));
        }
      } else {
        console.log('AreaSelector: キャッシュなし');
        // エリア設定がない場合は開いた状態にする
        setExpanded(true);
        setInitializationState(prev => ({ ...prev, cacheLoaded: true }));
      }
    } catch (err) {
      console.error('AreaSelector: キャッシュ読み込みエラー', err);
      localStorage.removeItem('areaSettings');
      // エラー時は開いた状態にする
      setExpanded(true);
      setInitializationState(prev => ({ ...prev, cacheLoaded: true }));
    }
  };

  useEffect(() => {
    // 都道府県データを取得
    const fetchPrefectures = async () => {
      try {
        console.log('AreaSelector: 都道府県データ取得中...');
        const response = await safeApiCall.get('/api/areas/prefectures');
        
        console.log('AreaSelector: 都道府県データ取得完了', response.data?.length);
        setPrefectures(response.data);
        setLoading(false);
        
        // 都道府県データ読み込み完了をマーク
        setInitializationState(prev => ({ ...prev, prefecturesLoaded: true }));
        
        // 都道府県データが取得できたら、キャッシュを読み込む
        loadCachedSettings();
      } catch (error) {
        console.error('AreaSelector: 都道府県データ取得エラー', error);
        setError('都道府県データの取得に失敗しました');
        setLoading(false);
        setInitializationState(prev => ({ ...prev, prefecturesLoaded: true, cacheLoaded: true }));
      }
    };

    fetchPrefectures();
  }, [singleCitySelect]);

  // 都道府県が選択されたときに市区町村を取得
  useEffect(() => {
    const fetchCities = async () => {
      if (!selectedPrefecture) {
        setCities([]);
        setInitializationState(prev => ({ ...prev, citiesLoaded: true }));
        return;
      }

      try {
        console.log('AreaSelector: 市区町村データ取得中...', selectedPrefecture);
        const response = await safeApiCall.get(`/api/areas/cities/${selectedPrefecture}`);
        
        console.log('AreaSelector: 市区町村データ取得完了', response.data?.length);
        setCities(response.data);
        setError(null);
        
        // 市区町村データ読み込み完了をマーク
        setInitializationState(prev => ({ ...prev, citiesLoaded: true }));
      } catch (error) {
        console.error('AreaSelector: 市区町村データ取得エラー', error);
        setError('市区町村データの取得に失敗しました');
        setCities([]);
        setInitializationState(prev => ({ ...prev, citiesLoaded: true }));
      }
    };

    fetchCities();
  }, [selectedPrefecture]);

  // 初期化完了後に一度だけonAreaChangeを呼ぶuseEffect
  useEffect(() => {
    const { prefecturesLoaded, citiesLoaded, cacheLoaded, onAreaChangeCalled } = initializationState;
    
    // 初期化完了の条件を詳細にチェック
    let initializationComplete = false;
    
    if (prefecturesLoaded && cacheLoaded && !onAreaChangeCalled) {
      // 都道府県が選択されていない場合（全国対象）
      if (!selectedPrefecture || selectedPrefecture === '') {
        console.log('AreaSelector: 都道府県なし（全国対象）- 初期化完了');
        initializationComplete = true;
      } else {
        // 市区町村データが必要かどうかを判定
        const needsCityData = selectedCity && selectedCity !== '';
        
        if (needsCityData) {
          // 市区町村データが必要な場合は、citiesLoaded かつ cities配列に実際にデータが存在することを確認
          initializationComplete = citiesLoaded && cities.length > 0;
        } else {
          // 市区町村データが不要な場合（都道府県のみ選択）は、citiesLoadedのみ確認
          initializationComplete = citiesLoaded;
        }
      }
    }
    
    if (initializationComplete) {
      console.log('AreaSelector: 初期化完了 - onAreaChangeを呼び出します');
      console.log('AreaSelector: 初期化状態詳細:', {
        prefecturesLoaded,
        citiesLoaded,
        cacheLoaded,
        onAreaChangeCalled,
        selectedPrefecture,
        selectedCity,
        selectedCities,
        singleCitySelect,
        prefecturesCount: prefectures.length,
        citiesCount: cities.length,
        needsCityData: selectedCity && selectedCity !== ''
      });
      
      // 都道府県データの安全性チェック
      console.log('AreaSelector: prefecturesデータ型確認:', { 
        type: typeof prefectures, 
        isArray: Array.isArray(prefectures), 
        length: prefectures?.length,
        content: prefectures 
      });
      
      const selectedPrefectureData = Array.isArray(prefectures) 
        ? prefectures.find(p => p.code === selectedPrefecture)
        : null;
      console.log('AreaSelector: 選択された都道府県データ:', selectedPrefectureData);
      
      if (singleCitySelect) {
        // 単一選択モード
        console.log('AreaSelector: 単一選択モード - selectedCity:', selectedCity);
        
        if (selectedCity) {
          // 市区町村が選択されている場合
          const selectedCityData = Array.isArray(cities) 
            ? cities.find(c => c.code === selectedCity)
            : null;
          console.log('AreaSelector: 選択された市区町村データ:', selectedCityData);
                      console.log('AreaSelector: 利用可能な市区町村一覧:', Array.isArray(cities) ? cities.map(c => ({ code: c.code, name: c.name })) : 'Not an array');
          
          if (selectedCityData && selectedPrefectureData) {
            console.log('AreaSelector: 初期化完了時のonAreaChange呼び出し（都道府県+市区町村）', {
              prefecture: selectedPrefectureData,
              city: selectedCityData
            });
            
            onAreaChange?.({ 
              prefecture: selectedPrefectureData, 
              city: selectedCityData
            });
          } else {
            console.log('AreaSelector: onAreaChange呼び出しスキップ - データ不足:', {
              selectedCityData: !!selectedCityData,
              selectedPrefectureData: !!selectedPrefectureData,
              citiesLength: cities.length
            });
            
            // 市区町村データが見つからない場合は都道府県のみで呼び出し
            if (selectedPrefectureData) {
              console.log('AreaSelector: 市区町村データなし - 都道府県のみで呼び出し');
              onAreaChange?.({ 
                prefecture: selectedPrefectureData, 
                city: null
              });
            }
          }
        } else {
          // 市区町村が選択されていない場合（都道府県のみ）
          if (selectedPrefectureData) {
            console.log('AreaSelector: 初期化完了時のonAreaChange呼び出し（都道府県のみ）', {
              prefecture: selectedPrefectureData,
              city: null
            });
            
            onAreaChange?.({ 
              prefecture: selectedPrefectureData, 
              city: null
            });
          } else {
            // 都道府県が選択されていない場合（全国対象）
            console.log('AreaSelector: 都道府県なし（全国対象）- onAreaChange呼び出し');
            onAreaChange?.({ 
              prefecture: null, 
              city: null
            });
          }
        }
      } else {
        // 複数選択モード
        console.log('AreaSelector: 複数選択モード - selectedCities:', selectedCities);
        
        if (selectedCities.length > 0) {
          const selectedCitiesWithNames = selectedCities.map(cityCode => {
            const city = Array.isArray(cities) 
              ? cities.find(c => c.code === cityCode)
              : null;
            return city || { code: cityCode, name: cityCode };
          });
          
          console.log('AreaSelector: 初期化完了時のonAreaChange呼び出し（都道府県+市区町村・複数選択）', {
            prefecture: selectedPrefectureData,
            cities: selectedCitiesWithNames
          });
          
          onAreaChange?.({ 
            prefecture: selectedPrefectureData, 
            cities: selectedCitiesWithNames 
          });
        } else {
          // 市区町村が選択されていない場合（都道府県のみ）
          if (selectedPrefectureData) {
            console.log('AreaSelector: 初期化完了時のonAreaChange呼び出し（都道府県のみ・複数選択モード）', {
              prefecture: selectedPrefectureData,
              cities: []
            });
            
            onAreaChange?.({ 
              prefecture: selectedPrefectureData, 
              cities: [] 
            });
          } else {
            // 都道府県が選択されていない場合（全国対象）
            console.log('AreaSelector: 都道府県なし（全国対象）- onAreaChange呼び出し（複数選択）');
            onAreaChange?.({ 
              prefecture: null, 
              cities: [] 
            });
          }
        }
      }
      
      // onAreaChange呼び出し完了をマーク
      setInitializationState(prev => ({ ...prev, onAreaChangeCalled: true }));
      console.log('AreaSelector: onAreaChange呼び出し完了をマーク');
    } else {
      // 初期化完了条件を満たしていない場合のログ
      if (prefecturesLoaded || citiesLoaded || cacheLoaded) {
        const needsCityData = selectedCity && selectedCity !== '';
        console.log('AreaSelector: 初期化完了待機中:', {
          prefecturesLoaded,
          citiesLoaded,
          cacheLoaded,
          onAreaChangeCalled,
          selectedPrefecture: !!selectedPrefecture,
          needsCityData,
          citiesLength: cities.length,
          waitingFor: !prefecturesLoaded ? 'prefectures' : 
                     !cacheLoaded ? 'cache' :
                     needsCityData && cities.length === 0 ? 'city data' :
                     onAreaChangeCalled ? 'already called' : 'unknown'
        });
      }
    }
  }, [initializationState, prefectures, cities, selectedPrefecture, selectedCity, selectedCities, singleCitySelect]);

  // 設定をキャッシュに保存
  const saveSettingsToCache = (prefecture, cities, city, ageConfirmed) => {
    try {
      const settings = {
        prefecture,
        cities,
        city,
        ageConfirmed,
        timestamp: new Date().getTime()
      };
      localStorage.setItem('areaSettings', JSON.stringify(settings));
    } catch (err) {
      
    }
  };

  // 都道府県選択時の処理
  const handlePrefectureChange = (event) => {
    const prefecture = event.target.value;
    console.log('AreaSelector: 都道府県変更:', prefecture);
    setSelectedPrefecture(prefecture);
    
    if (singleCitySelect) {
      // 単一選択モード - 市区町村をクリア
      setSelectedCity('');
      
      // キャッシュをクリアして都道府県のみの状態で保存
      saveSettingsToCache(prefecture, [], '', isAgeConfirmed);
      
      // 都道府県選択状態でonAreaChangeを呼ぶ
      const selectedPrefectureData = prefecture ? prefectures.find(p => p.code === prefecture) : null;
      console.log('AreaSelector: onAreaChange呼び出し（単一選択）:', { prefecture: selectedPrefectureData, city: null });
      onAreaChange?.({ 
        prefecture: selectedPrefectureData,
        city: null
      });
    } else {
      // 複数選択モード - 市区町村をクリア
      setSelectedCities([]);
      
      // キャッシュをクリアして都道府県のみの状態で保存
      saveSettingsToCache(prefecture, [], '', isAgeConfirmed);
      
      // 都道府県選択状態でonAreaChangeを呼ぶ
      const selectedPrefectureData = prefecture ? prefectures.find(p => p.code === prefecture) : null;
      console.log('AreaSelector: onAreaChange呼び出し（複数選択）:', { prefecture: selectedPrefectureData, cities: [] });
      onAreaChange?.({ 
        prefecture: selectedPrefectureData,
        cities: [] 
      });
    }
  };

  // 市区町村選択時の処理（複数選択）
  const handleCityChange = (event) => {
    const selectedCityCodes = event.target.value;
    setSelectedCities(selectedCityCodes);
    
    // 選択された都道府県と市区町村の情報を取得
    const selectedPrefectureData = prefectures.find(p => p.code === selectedPrefecture);
    const selectedCitiesData = selectedCityCodes.map(cityCode => 
      cities.find(c => c.code === cityCode)
    );
    
    onAreaChange?.({ 
      prefecture: selectedPrefectureData,
      cities: selectedCitiesData 
    });
    saveSettingsToCache(selectedPrefecture, selectedCityCodes, '', isAgeConfirmed);
  };

  // 市区町村選択時の処理（単一選択）
  const handleSingleCityChange = (event) => {
    const cityCode = event.target.value;
    setSelectedCity(cityCode);
    
    // 選択された都道府県と市区町村の情報を取得
    const selectedPrefectureData = prefectures.find(p => p.code === selectedPrefecture);
    const selectedCityData = cities.find(c => c.code === cityCode);
    
    onAreaChange?.({ 
      prefecture: selectedPrefectureData,
      city: selectedCityData
    });
    saveSettingsToCache(selectedPrefecture, [], cityCode, isAgeConfirmed);
  };

  // 年齢確認チェックボックスの処理
  const handleAgeConfirmChange = (event) => {
    const confirmed = event.target.checked;
    setIsAgeConfirmed(confirmed);
    onAgeConfirm?.(confirmed);
    
    if (singleCitySelect) {
      saveSettingsToCache(selectedPrefecture, [], selectedCity, confirmed);
    } else {
      saveSettingsToCache(selectedPrefecture, selectedCities, '', confirmed);
    }
  };

  // アコーディオンの展開状態を切り替え
  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  };

  // 選択された都道府県の名前を取得
  const getSelectedPrefectureName = () => {
    if (!selectedPrefecture) return '';
    // prefecturesが配列でない、または空の場合は空文字を返す
    if (!Array.isArray(prefectures) || prefectures.length === 0) return '';
    const prefecture = prefectures.find(p => p && p.code === selectedPrefecture);
    return prefecture ? prefecture.name : '';
  };

  // 選択された市区町村の名前を取得（複数選択）
  const getSelectedCityNames = () => {
    if (!Array.isArray(selectedCities) || selectedCities.length === 0) {
      return [];
    }
    
    if (!Array.isArray(cities) || cities.length === 0) {
      return selectedCities.map(cityCode => cityCode);
    }
    
    return selectedCities.map(cityCode => {
      if (!cityCode) return '';
      const city = cities.find(c => c && c.code === cityCode);
      return city ? city.name : cityCode;
    });
  };

  // 選択された市区町村の名前を取得（単一選択）
  const getSelectedCityName = () => {
    if (!selectedCity) return '';
    if (!Array.isArray(cities) || cities.length === 0) return '';
    const city = cities.find(c => c && c.code === selectedCity);
    return city ? city.name : '';
  };

  // アコーディオンサマリーのタイトルを生成
  const getSummaryTitle = () => {
    try {
      const prefName = getSelectedPrefectureName();
      if (!prefName) return 'エリア設定';
      
      if (singleCitySelect) {
        // 単一選択モード
        const cityName = getSelectedCityName();
        if (!cityName) return `エリア設定 - ${prefName}`;
        return `エリア設定 - ${prefName}（${cityName}）`;
      } else {
        // 複数選択モード
        const cityNames = getSelectedCityNames();
        if (!Array.isArray(cityNames) || cityNames.length === 0) return `エリア設定 - ${prefName}`;
        
        // 市区名のリスト（最初の3つまで表示）
        let cityText = cityNames.filter(name => name).slice(0, 3).join('、');
        
        // 4つ以上選択されている場合は残りの数を表示
        if (cityNames.length > 3) {
          cityText += `他${cityNames.length - 3}件`;
        }
        
        return `エリア設定 - ${prefName}（${cityText}）`;
      }
    } catch (err) {
      
      return 'エリア設定';
    }
  };

  if (loading) {
    return <Typography>読み込み中...</Typography>;
  }

  return (
    <Accordion expanded={expanded} onChange={handleAccordionChange}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="area-settings-content"
        id="area-settings-header"
      >
        <Typography>{getSummaryTitle()}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ p: 2 }}>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>都道府県</InputLabel>
            <Select
              value={selectedPrefecture}
              onChange={handlePrefectureChange}
              label="都道府県"
            >
              <MenuItem value="">選択なし</MenuItem>
              {Array.isArray(prefectures) && prefectures.map((prefecture) => (
                <MenuItem key={prefecture.code} value={prefecture.code}>
                  {prefecture.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {singleCitySelect ? (
            // 単一選択モード
            <FormControl fullWidth sx={{ mb: 2 }} disabled={!selectedPrefecture}>
              <InputLabel>市区町村</InputLabel>
              <Select
                value={selectedCity}
                onChange={handleSingleCityChange}
                label="市区町村"
              >
                <MenuItem value="">選択なし</MenuItem>
                {Array.isArray(cities) && cities.map((city) => (
                  <MenuItem key={city.code} value={city.code}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                都道府県を選択すると市区町村が表示されます
              </FormHelperText>
            </FormControl>
          ) : (
            // 複数選択モード
            <FormControl fullWidth sx={{ mb: 2 }} disabled={!selectedPrefecture}>
              <InputLabel>市区町村</InputLabel>
              <Select
                multiple
                value={selectedCities || []}
                onChange={handleCityChange}
                input={<OutlinedInput label="市区町村" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {Array.isArray(selected) && selected.map((value) => {
                      const city = Array.isArray(cities) && cities.find(c => c && c.code === value);
                      return (
                        <Chip key={value} label={city ? city.name : value} />
                      );
                    })}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {Array.isArray(cities) && cities.map((city) => (
                  <MenuItem key={city.code} value={city.code}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                都道府県を選択すると市区町村が表示されます（複数選択可能）
              </FormHelperText>
            </FormControl>
          )}

          {isGuestUser && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAgeConfirmed}
                  onChange={handleAgeConfirmChange}
                  color="primary"
                />
              }
              label="18歳以上であることを確認します"
            />
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default AreaSelector; 